import { createAction, createReducer, PayloadAction } from '@reduxjs/toolkit';
import { extInfo } from 'lib/extInfo';
import { path } from 'ramda';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getExtension, getItemNameWithoutExt, getParent, isFolder, normalizeId, normalizePublicApiInfo } from 'reactApp/modules/file/utils';
import {
    addFilesSuccess,
    addToFavoritesSuccess,
    moveItemsSuccess,
    publishWeblink,
    removeFileSuccess,
    removeFromFavoritesSuccess,
    renameItemSuccess,
    resetWeblinkCountDownloads,
    shareFolderSuccess,
    toggleWeblinkAccessRights,
    toggleWeblinkDomestic,
    toggleWeblinkDownloadable,
    unPublishWeblink,
    unshareFolderSuccess,
    updateWeblinkAutoDelete,
    updateWeblinkCountDownloads,
    updateWeblinkExpires,
} from 'reactApp/modules/modifying/modifying.actions';
import {
    AddFileSuccessAction,
    AddItem,
    IAddToFavoritesSuccess,
    IRemoveFromFavoritesSuccess,
    IRenameItemSuccess,
    IShareFolderSuccess,
    IUnshareFolderSuccess,
    MoveItemsSuccessAction,
    RemoveFileSuccessAction,
    ResetWeblinkCountDownloads,
    ToggleWeblinkAccessRights,
    ToggleWeblinkDomestic,
    ToggleWeblinkDownloadable,
    UnpublishWeblinkAction,
    UpdateWeblinkAction,
    UpdateWeblinkAutoDelete,
    UpdateWeblinkCountDownloads,
    UpdateWeblinkExpires,
} from 'reactApp/modules/modifying/modifying.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UrlBuilder } from 'reactApp/modules/urlBuilder/UrlBuilder';
import { AccessRights } from 'reactApp/types/Tree';
import { normalizeMtime, renameCameraUploadFolder } from 'reactApp/utils/tree.helpers';

import { SEARCH_PER_PAGE_LIMIT } from './search.helpers';
import {
    ApiSearchFolderResponseFile,
    ApiSearchResponceList,
    ApiSearchResponseFolder,
    IHistoryItem,
    SearchItem,
    SearchRequestParams,
    SearchState,
    VirusScanResponce,
} from './search.types';

const urlBuilder = new UrlBuilder();

const initialState: SearchState = {
    [ROOT_FOLDER_ID]: {
        id: '/',
        isLoading: false,
        isLoaded: false,
        count: {
            all: 0,
            loaded: 0,
            files: 0,
            folders: 0,
        },
        hasMoreToLoad: false,
        error: false,
        childs: [],
        name: 'Результаты поиска',
    },
    list: {},
    listFull: [],
    requestParams: {
        query: '',
        xPageId: 'initial_empty_value',
        xReqId: 'initial_empty_value',
    },
    offset: 0,
    searchStartLocation: { id: '/home', search: '' },
    searchHistory: [],
};

export const searchLoadRequestStart = createAction('search/load/requestStart');
export const searchLoadRequest = createAction('search/load/request');
export const searchLoadStart = createAction<ApiSearchResponceList>('search/load/start');
export const searchLoadSuccess = createAction('search/load/success');
export const searchLoadError = createAction('search/load/error');
export const searchLoadHistorySuccess = createAction<IHistoryItem[]>('search/load/historySuccess');
export const searchLoadHistoryError = createAction('search/load/historyError');
export const searchLoadMoreRequest = createAction('search/loadMore/request');
export const searchLoadMoreSuccess = createAction<number>('search/loadMore/success');
export const searchLoadHistory = createAction('search/load/history');
export const searchRemoveItemHistory = createAction<string>('search/remove/history');
export const searchClearHistory = createAction('search/clear/history');
export const searchRequestParamsUpdate = createAction<SearchRequestParams>('search/update/params');
export const searchUpdateDomainFolders = createAction<string[]>('search/update/domainFolders');
export const searchUpdateVirusScan = createAction<VirusScanResponce>('search/update/virusScan');
export const searchUpdateStartLocation = createAction<{ id: string; search: string }>('search/update/startLocation');

const normalize = (item: ApiSearchResponseFolder | ApiSearchFolderResponseFile | AddItem | SearchItem, index?: number): SearchItem => {
    if ('__reduxTree' in item && item.__reduxTree) {
        return item;
    }

    item.name = item.name ?? normalizeId(item.home).split('/').pop();
    const ext = getExtension(item);
    const nameWithoutExt = getItemNameWithoutExt(item.name, ext);
    const { home, name } = item;

    const normalizedItem = {
        pos: index,
        home,
        parentFolder: getParent(home || '') || '',
        id: home || '',
        name,
        nameWithoutExt,
        nameParts: 'name_parts' in item ? item.name_parts : undefined,
        ...normalizePublicApiInfo(item),
    };

    if (isFolder(item)) {
        return {
            ...normalizedItem,
            kind: item.kind || 'folder',
            name: renameCameraUploadFolder(item),
            __reduxTree: true,
            parent: '/',
            isFolder: true,
            storage: EStorageType.search,
        };
    }

    const { subKind, kind } = extInfo.get(ext);
    const { illegal, blocked, hash, size, mtime, virus_scan } = item as ApiSearchFolderResponseFile;

    return {
        ...normalizedItem,
        isFolder: false,
        storage: EStorageType.search,
        isIllegal: !!illegal,
        isBlocked: !!blocked,
        isInFavorites: !!path(['favorite'], item) || path(['isInFavorites'], item),
        hash,
        parent: '/',
        kind,
        subKind,
        __reduxTree: true,
        size,
        mtime: normalizeMtime(mtime) || Date.now(),
        virusScan: virus_scan,
        ext,
        thumbnails: urlBuilder.getThumb({
            hash,
            ext,
            id: normalizedItem.id,
            size,
            kind,
            name,
            path: home,
            isPublic: false,
            isStock: false,
            dwl_token: null,
            weblink: '',
        }),
        url: urlBuilder.getUrls({
            ext,
            isPublic: false,
            id: normalizedItem.id,
            weblink: '',
            size,
            kind,
            subKind,
            isStock: false,
            name,
            path: home,
            dwl_token: null,
        }),
    };
};

export const searchReducer = createReducer(initialState, {
    [searchUpdateStartLocation.type]: (state, action: PayloadAction<{ id: string; search: string }>) => {
        state.searchStartLocation = action.payload;
    },
    [searchLoadRequest.type]: (state) => {
        state[ROOT_FOLDER_ID].isLoading = true;
        state[ROOT_FOLDER_ID].isLoaded = false;
        state[ROOT_FOLDER_ID].error = false;
    },
    [searchLoadStart.type]: (state, action: PayloadAction<ApiSearchResponceList>) => {
        state.listFull = action.payload;
    },
    [searchUpdateDomainFolders.type]: (state, action: PayloadAction<string[]>) => {
        const domainFoldersIDs = action.payload;
        state.listFull.forEach((item) => {
            if (domainFoldersIDs.includes(item.home)) {
                item.kind = 'domain-folder';
            }
        });
    },
    [searchUpdateVirusScan.type]: (state, action: PayloadAction<VirusScanResponce>) => {
        const virusScanResults = action.payload;
        state.listFull.forEach((item) => {
            if ('hash' in item && item?.hash && virusScanResults[item.hash]) {
                item = {
                    ...item,
                    ...virusScanResults[item.hash],
                };
            }
        });
    },
    [searchLoadSuccess.type]: (state) => {
        state[ROOT_FOLDER_ID].childs = [];
        state[ROOT_FOLDER_ID].isLoading = false;
        state[ROOT_FOLDER_ID].isLoaded = true;

        /**
         * У текущей ручки поиска нет возможности для пагинации (offset или cursor), есть только limit.
         * Грузим весь список результатов и сохраняемм в сторе. Это нужно будет для loadMore.
         * Изначально показываем первую пачку результатов (см. SEARCH_PER_PAGE_LIMIT).
         */
        const limitedList = state.listFull.slice(0, SEARCH_PER_PAGE_LIMIT);

        let folders = 0;
        let files = 0;

        // Добавляем кол-во файлов и папок в результаты поиска (нужно для пагинации).
        state.listFull.forEach((item) => {
            if (item.kind === 'file') {
                files++;
            } else {
                folders++;
            }
        });

        const count = {
            folders,
            files,
            all: state.listFull.length,
            loaded: limitedList.length,
        };

        state[ROOT_FOLDER_ID].count = count;
        state[ROOT_FOLDER_ID].hasMoreToLoad = count.loaded < count.all;

        state.offset = limitedList.length;

        limitedList.forEach((item, index) => {
            const normalizedItem = normalize({ ...item }, index);
            state.list[normalizedItem.id] = normalizedItem;
            state[ROOT_FOLDER_ID].childs.push(normalizedItem.id);
        });
    },
    [searchLoadError.type]: (state) => {
        state[ROOT_FOLDER_ID].childs = [];
        state[ROOT_FOLDER_ID].isLoading = false;
        state[ROOT_FOLDER_ID].isLoaded = true;
        state[ROOT_FOLDER_ID].error = true;
    },
    [searchLoadMoreRequest.type]: (state) => {
        state[ROOT_FOLDER_ID].isLoading = true;
    },
    [searchLoadMoreSuccess.type]: (state, action: PayloadAction<number>) => {
        const prevCount = state[ROOT_FOLDER_ID].count.loaded;
        const loaded = prevCount + action.payload;
        state[ROOT_FOLDER_ID].isLoading = false;
        state[ROOT_FOLDER_ID].count.loaded = loaded;
        state[ROOT_FOLDER_ID].hasMoreToLoad = loaded < state[ROOT_FOLDER_ID].count.all;

        const nextList = state.listFull.slice(state.offset, state.offset + SEARCH_PER_PAGE_LIMIT);

        nextList.forEach((item, index) => {
            const normalizedItem = normalize({ ...item }, prevCount + index);
            state.list[normalizedItem.id] = normalizedItem;
            state[ROOT_FOLDER_ID].childs.push(normalizedItem.id);
        });

        const newOffset = state.offset + SEARCH_PER_PAGE_LIMIT;
        state.offset = newOffset < state.listFull.length ? newOffset : state.listFull.length;
    },
    [searchRequestParamsUpdate.type]: (state, action: PayloadAction<SearchRequestParams>) => {
        state.requestParams = {
            ...state.requestParams,
            ...action.payload,
        };
    },
    [addFilesSuccess.type]: (state, action: PayloadAction<AddFileSuccessAction>) => {
        const { items, storage } = action.payload;

        if (storage !== EStorageType.search) {
            return;
        }

        items.forEach((item) => {
            const normalizedItem = normalize({ ...item });

            if (state.list[normalizedItem.id]) {
                return;
            }

            state[ROOT_FOLDER_ID].childs.push(normalizedItem.id);
            state.list[normalizedItem.id] = normalizedItem;
        });
    },
    [renameItemSuccess.type]: (state, action: PayloadAction<IRenameItemSuccess>) => {
        const { newItem, oldId } = action.payload;
        const file = newItem as SearchItem;

        if (!file || !oldId) {
            return;
        }

        state[ROOT_FOLDER_ID].childs = state[ROOT_FOLDER_ID].childs.filter((child) => child !== oldId);
        delete state.list[oldId];

        state[ROOT_FOLDER_ID].childs.push(file.id);
        state.list[file.id] = file;
    },
    [moveItemsSuccess.type]: (state, action: PayloadAction<MoveItemsSuccessAction>) => {
        const { newItems, oldIds, storage } = action.payload;

        if (storage !== EStorageType.search) {
            return;
        }

        newItems.forEach((newItem, index) => {
            const file = newItem as SearchItem;
            const oldItemId = oldIds[index];
            const oldItem = state.list[oldItemId];

            if (!file?.id || !oldItem || state[file?.id]) {
                return;
            }

            state.list[file.id] = file;
            state[ROOT_FOLDER_ID].childs.push(file.id);

            state[ROOT_FOLDER_ID].childs = state[ROOT_FOLDER_ID].childs.filter((child) => child !== oldItemId);
            delete state.list[oldItemId];
        });
    },
    [removeFileSuccess.type]: (state, action: PayloadAction<RemoveFileSuccessAction>) => {
        const { ids } = action.payload;

        state[ROOT_FOLDER_ID].childs = state[ROOT_FOLDER_ID].childs.filter((child) => !ids.includes(child));

        ids.forEach((id) => {
            delete state.list[id];
        });
    },
    [publishWeblink.type]: (state, action: PayloadAction<UpdateWeblinkAction>) => {
        const { weblink, id } = action.payload;

        if (state.list[id]) {
            state.list[id].weblink = weblink;
        }
    },
    [unPublishWeblink.type]: (state, action: PayloadAction<UnpublishWeblinkAction>) => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];

            if (!item) {
                return;
            }

            item.weblink = undefined;
            item.weblinkAccessRights = AccessRights.r;
            item.weblinkDomestic = false;
            item.weblinkExpires = 0;

            if (item.isFolder) {
                item.weblinkAutoDelete = false;
            }
        });
    },
    [toggleWeblinkAccessRights.type]: (state, action: PayloadAction<ToggleWeblinkAccessRights>): void => {
        const { id, type } = action.payload;
        if (state.list[id]) {
            state.list[id].weblinkAccessRights = type;
        }
    },
    [toggleWeblinkDownloadable.type]: (state, action: PayloadAction<ToggleWeblinkDownloadable>): void => {
        const { id, downloadable } = action.payload;

        if (state.list[id]) {
            state.list[id].weblinkDownloadable = downloadable;
        }
    },
    [updateWeblinkCountDownloads.type]: (state, action: PayloadAction<UpdateWeblinkCountDownloads>): void => {
        const { id, count_downloads } = action.payload;
        const item = state.list[id];
        if (item) {
            item.count_downloads_left = count_downloads;
            item.count_downloads_total = count_downloads;
        }
    },
    [resetWeblinkCountDownloads.type]: (state, action: PayloadAction<ResetWeblinkCountDownloads>): void => {
        const { id } = action.payload;

        if (state.list[id]) {
            state.list[id].count_downloads_total = undefined;
            state.list[id].count_downloads_left = undefined;
        }
    },
    [toggleWeblinkDomestic.type]: (state, action: PayloadAction<ToggleWeblinkDomestic>): void => {
        const { id, domestic } = action.payload;

        if (state.list[id]) {
            state.list[id].weblinkDomestic = domestic;
        }
    },
    [updateWeblinkExpires.type]: (state, action: PayloadAction<UpdateWeblinkExpires>): void => {
        const { id, expires } = action.payload;

        if (state.list[id]) {
            state.list[id].weblinkExpires = expires;
        }
    },
    [updateWeblinkAutoDelete.type]: (state, action: PayloadAction<UpdateWeblinkAutoDelete>): void => {
        const { id, autoDelete } = action.payload;
        const item = state.list[id];

        if (item && item.isFolder) {
            item.weblinkAutoDelete = autoDelete;
        }
    },
    [unshareFolderSuccess.type]: (state, action: PayloadAction<IUnshareFolderSuccess>) => {
        const { id } = action.payload;
        const item = state.list[id];

        if (!item || !isFolder(item)) {
            return;
        }

        item.kind = 'folder';
    },
    [shareFolderSuccess.type]: (state, action: PayloadAction<IShareFolderSuccess>) => {
        const { id } = action.payload;
        const item = state.list[id];

        if (!item || !isFolder(item)) {
            return;
        }

        item.kind = 'shared';
    },
    [addToFavoritesSuccess.type]: (state, action: PayloadAction<IAddToFavoritesSuccess>): void => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];

            if (!item) {
                return;
            }

            item.isInFavorites = true;
        });
    },
    [removeFromFavoritesSuccess.type]: (state, action: PayloadAction<IRemoveFromFavoritesSuccess>): void => {
        const { ids } = action.payload;

        ids.forEach((id) => {
            const item = state.list[id];

            if (!item) {
                return;
            }

            item.isInFavorites = false;
        });
    },
    [searchLoadHistorySuccess.type]: (state, action: PayloadAction<IHistoryItem[]>): void => {
        state.searchHistory = action.payload;
    },
    [searchLoadHistoryError.type]: (state): void => {
        state.searchHistory = [];
    },
    [searchRemoveItemHistory.type]: (state, action: PayloadAction<string>): void => {
        state.searchHistory = state.searchHistory.filter((item) => item.id !== action.payload);
    },
    [searchClearHistory.type]: (state): void => {
        state.searchHistory = [];
    },
});
